import {FC, useState} from 'react';
import cn from 'classnames';
import {PhoneInput} from '@rocket-ui/components';
import {useLogin} from '@rocket-ui/lk-api';
import {Button} from 'ui';
import {isFinance} from 'shared/lib/isFinance';
import styles from './PhoneInputForm.module.css';

interface PhoneInputForm {
	onSubmit: (phone: string, codeValidationToken: string, usedOtpStrategy?: string | null) => void;
}

const DUID_STORAGE_KEY = 'lk-device-id';

const PERSONAL_DATA_POLICY_URL = 'https://files.rocketwork.ru/roketwork/Politika_obrabotki_PD.pdf';
const SERVICE_USAGE_AGREEMENT_URL = 'https://files.rocketwork.ru/roketwork/Soglashenie_Rocket_Work.pdf';
const FINANCE_SERVICE_USAGE_AGREEMENT_URL =
	'https://files.rocketwork.ru/roketwork/roketworksoglashenie-rocketfinance.pdf';

function getOrCreateDuid() {
	if (typeof window === 'undefined') return 'lk-web';

	const savedDuid = window.localStorage.getItem(DUID_STORAGE_KEY);

	if (savedDuid) return savedDuid;

	const duid = window.crypto?.randomUUID?.() ?? `${Date.now()}`;

	window.localStorage.setItem(DUID_STORAGE_KEY, duid);

	return duid;
}

export const PhoneInputForm: FC<PhoneInputForm> = ({onSubmit}) => {
	const [phone, setPhone] = useState('+7');
	const [duid] = useState(getOrCreateDuid);
	const {mutateAsync: login, isLoading, error, reset} = useLogin();

	const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setPhone(e.target.value);
		reset();
	};

	const handleSubmit = async () => {
		const {authToken, usedOtpStrategy} = await login({phone, duid});
		onSubmit(phone, authToken, usedOtpStrategy);
	};

	const handleSubmitPhone = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key !== 'Enter') return;

		return handleSubmit();
	};

	const isValidPhoneNumber = phone.length > 8 && phone.replace(/\D/g, '').length <= 14;
	const buttonIsVisible = phone.startsWith('+7') ? phone.length === 12 : isValidPhoneNumber;
	const serviceUsageAgreementUrl = isFinance()
		? FINANCE_SERVICE_USAGE_AGREEMENT_URL
		: SERVICE_USAGE_AGREEMENT_URL;

	return (
		<section className={styles.section}>
			<p className={styles.title}>Введите номер телефона</p>
			<div className={styles.fields}>
				<PhoneInput
					name={'phone'}
					value={phone}
					className={styles.phoneInput}
					classes={{
						root: styles.phoneRoot,
						container: styles.phoneContainer,
						nativeInput: styles.phoneNativeInput,
					}}
					onChange={handlePhoneChange}
					onKeyDown={handleSubmitPhone}
					autoComplete="tel"
					autoFocus
				/>
				{error && <p className={styles.error}>{error.response?.data.errors?.[0] ?? error.message}</p>}
			</div>
			<div className={cn(styles.submitBar, !buttonIsVisible && styles.submitBar_reserved)}>
				<p className={styles.legal}>
					Нажимая на кнопку «Получить код», вы принимаете{' '}
					<a
						href={PERSONAL_DATA_POLICY_URL}
						className={styles.legalLink}
						target="_blank"
						rel="noopener noreferrer"
					>
						Политику обработки персональных данных
					</a>{' '}
					и{' '}
					<a
						href={serviceUsageAgreementUrl}
						className={styles.legalLink}
						target="_blank"
						rel="noopener noreferrer"
					>
						Соглашение об использовании сервиса
					</a>
				</p>
				<Button
					type={'button'}
					fullWidth
					onClick={handleSubmit}
					isLoading={isLoading}
					disabled={!buttonIsVisible}
					tabIndex={buttonIsVisible ? undefined : -1}
					className={styles.submitButton}
				>
					Получить код
				</Button>
			</div>
		</section>
	);
};
